/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, MouseEvent, useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import {GenericObject} from '@ion/core/src/types/entities';
import {IconButton} from '../../components/buttons/IconButton';
import {
  BottomGridViewIcon
} from '../../icons/BottomGridViewIcon';
import {
  SideGridViewIcon
} from '../../icons/SideGridViewIcon';
import {
  SystemViewCardsIcon
} from '../../icons/SystemViewCardsIcon';
import {
  SystemViewListIcon
} from '../../icons/SystemViewListIcon';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {DLSTheme, IconSize, SideSheetView} from '../../types/types';

interface StyleProps {
  overrideStyles?: GenericObject;
};

interface SidesheetModeMenuProps extends StyleProps {
  size?: IconSize;
  onView: (event: MouseEvent) => void;
  sideSheetView: SideSheetView;
};

const getStyles = (className: string, property: string, fallbackStyle: string) =>
  ({overrideStyles}: StyleProps) => overrideStyles?.[className]?.[property] || fallbackStyle
;

const useStyles = makeStyles((theme: DLSTheme) => {
  const menuShadow = '2px 25px 25px 0 rgba(0, 0, 0, 0.10)';
  return {
    root : {
      backgroundColor : dlsColor('white'),
      overflow        : 'hidden',
      position        : 'relative',
      borderRadius    : 'none',
      marginTop       : getStyles('root', 'marginTop', 'initial'),
      '&:hover'       : {
        boxShadow  : menuShadow,
        overflow   : 'visible',
        transition : 'transform 0.3s ease-in-out'
      }
    },
    hoverMenu : {
      boxShadow       : menuShadow,
      backgroundColor : dlsColor('white'),
      position        : 'absolute',
      zIndex          : 100,
      display         : 'flex',
      flexDirection   : 'column',
      alignItems      : 'center',
      justifyContent  : 'center'
    },
    menuItem : {
      '&:hover' : {
        backgroundColor : dlsAlt('components', 'btn', 'iconBackground')
      }
    }
  };
});

/**
 * The SidesheetModeMenu component is used to display the side sheet view options
 * via a hoverable dropdown.
 *
 * ```typescript
 *
 * import SidesheetModeMenu from './SidesheetModeMenu';
 *
 * {isDesktop && (
 * <SidesheetModeMenu onView={onView}
 *   sideSheetView={sideSheetView} />
 * ...
 * )}
 *
 * ```
 */
const SidesheetModeMenu = ({
  size = IconSize.small,
  onView,
  sideSheetView,
  overrideStyles
}: SidesheetModeMenuProps) => {
  const classes = useStyles({overrideStyles});

  // modeTypeIconMap provides a map for the sidesheet mode icons.
  const modeTypeIconMap = useMemo(() => ({
    [SideSheetView.table]       : <SideGridViewIcon size={size} />,
    [SideSheetView.detailed]    : <SystemViewListIcon size={size} />,
    [SideSheetView.collapsed]   : <SystemViewCardsIcon size={size} />,
    [SideSheetView.bottomTable] : <BottomGridViewIcon size={size} />
  }), []);

  // getRemainingIcons returns the remaining icons that are not the current view.
  const getRemainingIcons = () => {
    return Object.values(SideSheetView).filter(mode => mode !== sideSheetView);
  };

  return (
    <div className={classes.root}>
      <div className={classes.menuItem}>
        <IconButton id={SideSheetView.detailed}
          color={dlsColor('grey3')}
          icon={modeTypeIconMap[sideSheetView]} />
      </div>
      <div className={classes.hoverMenu}>
        {getRemainingIcons()?.map((view) => (
          <div className={classes.menuItem}>
            <IconButton id={view}
              onClick={onView}
              color={dlsColor('grey3')}
              icon={modeTypeIconMap[view]} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default memo(SidesheetModeMenu);
